import { buildI18n } from "@repo/shared";
import type { LocalizedString, ProductCostClass } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import {
  useCreateProductCostClass,
  useDeleteProductCostClass,
  useToggleProductCostClassActive,
  useUpdateProductCostClass,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./product-cost-classes-table.i18n";

type ProductCostClassesTableProps = {
  portfolioCollectionId: string;
  productCostClasses: ProductCostClass[];
};

export function ProductCostClassesTable({
  portfolioCollectionId,
  productCostClasses,
}: ProductCostClassesTableProps) {
  const createClass = useCreateProductCostClass();
  const updateClass = useUpdateProductCostClass();
  const deleteClass = useDeleteProductCostClass();
  const toggleActive = useToggleProductCostClassActive();

  const isPending =
    createClass.isPending ||
    updateClass.isPending ||
    deleteClass.isPending ||
    toggleActive.isPending;

  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const getName = (i18n: LocalizedString) => localize(i18n);

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<ProductCostClass | null>(null);
  const [editName, setEditName] = useState("");

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    createClass.mutate(
      {
        collectionId: portfolioCollectionId,
        data: { nameI18n: buildI18n(newName, userLanguage, tenantSupportedLanguages) },
      },
      {
        onSuccess: () => {
          setNewName("");
          setIsAdding(false);
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToAdd, "destructive"),
      },
    );
  };

  const handleUpdate = () => {
    if (!editing || !editName.trim()) return;
    const updatedI18n: LocalizedString = { ...editing.nameI18n, [userLanguage]: editName };
    updateClass.mutate(
      { collectionId: portfolioCollectionId, pccId: editing.id, data: { nameI18n: updatedI18n } },
      {
        onSuccess: () => {
          setEditing(null);
          setEditName("");
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleTranslationSave = (pccId: string, nameI18n: LocalizedString) => {
    updateClass.mutate(
      { collectionId: portfolioCollectionId, pccId, data: { nameI18n } },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  // QL-168 — a cost class the server would refuse to delete shows why on the
  // Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (pccId) => pcApi.checkDeleteProductCostClass(portfolioCollectionId, pccId),
    onAllowed: (pccId) => {
      setToDelete(pccId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = () => {
    if (!toDelete) return;
    deleteClass.mutate(
      { collectionId: portfolioCollectionId, pccId: toDelete },
      {
        onSuccess: () => setToDelete(null),
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setToDelete(null);
        },
      },
    );
  };

  const handleToggleActive = (id: string) => {
    toggleActive.mutate(
      { collectionId: portfolioCollectionId, pccId: id },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{t.title}</h2>
            <p className="text-sm text-muted-foreground">{t.description}</p>
          </div>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)} disabled={isPending}>
              {t.addClass}
            </Button>
          )}
        </div>

        {productCostClasses.length === 0 && !isAdding ? (
          <EmptyState>{t.noClasses}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productCostClasses.map((cls) => (
                <TableRow key={cls.id}>
                  <TableCell>
                    {editing?.id === cls.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder={t.className}
                          autoFocus
                          disabled={isPending}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") {
                              setEditing(null);
                              setEditName("");
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={handleUpdate}
                          disabled={isPending}
                          loading={updateClass.isPending}
                        >
                          {t.save}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(null);
                            setEditName("");
                          }}
                          disabled={isPending}
                        >
                          {t.cancel}
                        </Button>
                      </div>
                    ) : (
                      <span className="font-medium">{getName(cls.nameI18n)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getName(cls.nameI18n)}`}
                      checked={cls.isActive}
                      onCheckedChange={() => handleToggleActive(cls.id)}
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {editing?.id !== cls.id && (
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          tooltip={t.edit}
                          variant="ghost"
                          onClick={() => {
                            setEditing(cls);
                            setEditName(getName(cls.nameI18n));
                          }}
                          disabled={isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <TranslationButton
                          value={cls.nameI18n}
                          onSave={(nameI18n) => handleTranslationSave(cls.id, nameI18n)}
                          supportedLanguages={tenantSupportedLanguages}
                          disabled={isPending}
                        />
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          loading={checkingId === cls.id}
                          onClick={() => requestDelete(cls.id)}
                          disabled={isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isAdding && (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t.placeholder}
              autoFocus
              disabled={isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") {
                  setIsAdding(false);
                  setNewName("");
                }
              }}
            />
            <Button size="sm" onClick={handleAdd} disabled={isPending}>
              {t.add}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setNewName("");
              }}
              disabled={isPending}
            >
              {t.cancel}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteClass}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
